/// <amd-module name="Artifacts/Root/Redux" />

import {
  ErrorComponentState,
  LoadingComponentState,
  ReadyComponentState,
} from "Artifacts/Root/helpers/ComponentStateHelpers";
import produce from "immer";
import * as React from "react";
import {
  createStore,
  DynamicModuleLoader,
  IModuleStore,
  IModuleTuple,
} from "redux-dynamic-modules";
import { Action, ActionsUnion, ActionWithPayload } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IReduxStoreService } from "VSS/Redux/StoreService";

/**
 * connectDynamicModules wraps component in a dynamic module loader that allows to connect redux storage on component rendering.
 *
 * @param Component React component.
 * @param modules List of dynamic modules that needs to be attached to component.
 */
export function connectDynamicModules<P = unknown, S = unknown, SS = unknown>(
  Component: React.ElementType,
  modules: IModuleTuple,
): React.ComponentFactory<P, React.Component<P, S, SS>> {
  return (props: P) => (
    <DynamicModuleLoader modules={modules}>
      <Component {...props} />
    </DynamicModuleLoader>
  );
}

/**
 * @copyright Copyright (c) 2018 Martin Hochel
 * Borrowed from the rex-tils library
 *
 * (reborrowed from "redux/Utils/ActionHelper")
 */
type ActionsCreatorsMapObject = {
  [actionCreator: string]: (...args: any[]) => any;
};

type ReducerHandlerMapType<StateT> = Record<string, (draft: StateT, payload: any) => void>;

export function createReducer<StateT, ActionT extends ActionsCreatorsMapObject>(
  initialState: StateT,
  handlers: ReducerHandlerMapType<StateT>,
): (currentState: StateT | undefined, a: ActionsUnion<ActionT>) => StateT {
  return produce((draft: StateT, action: ActionsUnion<ActionT>) => {
    if (Object.prototype.hasOwnProperty.call(handlers, action.type)) {
      handlers[action.type](draft, action.payload);
    }
  }, initialState);
}

/**
 * syncComponentState is a helper function for a reducer that helps to track component state
 * in sync with fired actions.
 * @param draftObject Immer store draft object.
 * @param componentStatePropName Component state property name, separated by dots for indexing nested objects
 * @param action Current action
 * @param actionTypeMapping Map of component states to action types.
 */
export function syncComponentState(
  draftObject: any,
  componentStatePropName: string,
  action: Action<any>,
  actionTypeMapping: {
    ready: ActionsUnion<any>;
    loading: ActionsUnion<any>;
    error: ActionsUnion<any>;
  },
): void {
  if (action.type === actionTypeMapping.ready) {
    setDraftObject(draftObject, componentStatePropName, ReadyComponentState());
  } else if (action.type === actionTypeMapping.error) {
    setDraftObject(
      draftObject,
      componentStatePropName,
      // Ideally we should find a way to enforce this at compile-time w/ generic types
      ErrorComponentState((action as ActionWithPayload<any, ILoadFailedPayload>).payload.error),
    );
  } else if (action.type === actionTypeMapping.loading) {
    setDraftObject(draftObject, componentStatePropName, LoadingComponentState());
  }
}

/**
 * Common interface for failed data load action payload
 */
export interface ILoadFailedPayload {
  error: string;
}

export function getStore(pageContext: IVssPageContext): IModuleStore<any> {
  const storeService = pageContext.getService<IReduxStoreService>("redux-store-service");
  return storeService.getStore(() => {
    return createStore({
      initialState: {},
      enhancers: [],
      extensions: [],
    });
  });
}

function setDraftObject(draftObject: any, propName: string, value: any) {
  propName.split(".").reduce((prevDraft, propPathSegment, i, a) => {
    if (i < a.length - 1) {
      return prevDraft[propPathSegment] || (prevDraft[propPathSegment] = {});
    } else {
      return (prevDraft[propPathSegment] = value);
    }
  }, draftObject);
}
